import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Upload, Sparkles, Wallet, Clock3, MessageCircle } from "lucide-react";

const FAIXA_CORES = {
  verde: "bg-[#DCFCE7] text-[#166534]", amarelo: "bg-[#FEF3C7] text-[#92400E]",
  laranja: "bg-orange-100 text-orange-700", vermelho: "bg-red-100 text-[#B91C1C]",
  critico: "bg-[#B91C1C] text-white",
};

const STATUS_CORES = {
  OFERTA_ENVIADA: "bg-[#DBEAFE] text-[#142766]",
  CONTRATO_PENDENTE: "bg-aj-amber-soft text-aj-orange-hover",
  AGUARDANDO_PAGAMENTO: "bg-[#FEF3C7] text-[#92400E]",
  DECLINADO_PELO_CLIENTE: "bg-red-100 text-[#B91C1C]",
  OFERTA_EXPIRADA_SEM_RESPOSTA: "bg-gray-100 text-gray-600",
  PEDIDO_ABERTO: "bg-[#DCFCE7] text-[#166534]",
  AGENDAMENTO_CRIADO: "bg-[#DCFCE7] text-[#166534]",
  MONTAGEM_CONCLUIDA: "bg-[#DCFCE7] text-[#166534]",
};

export default function ParticularOfertas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ativo, setAtivo] = useState(null);
  const [leads, setLeads] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [reembolsos, setReembolsos] = useState([]);
  const [ressarcimentos, setRessarcimentos] = useState([]);
  const [prazos, setPrazos] = useState([]);
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState("12");
  const [exO, setExO] = useState("2");
  const [exMa, setExMa] = useState("1");
  const [exMo, setExMo] = useState("7");
  const [enviando, setEnviando] = useState(false);

  const carregar = () => {
    api.get("/particular-modulo/status").then(r => setAtivo(r.data.ativo)).catch(() => {});
    api.get("/particular/leads").then(r => setLeads(r.data)).catch(() => {});
    api.get("/particular/ledger").then(r => setLedger(r.data)).catch(() => {});
    api.get("/particular/reembolsos").then(r => setReembolsos(r.data)).catch(() => {});
    api.get("/particular/ressarcimentos").then(r => setRessarcimentos(r.data)).catch(() => {});
    api.get("/particular/prazos").then(r => setPrazos(r.data)).catch(() => {});
  };
  useEffect(carregar, []);

  const efetivarReembolso = async (id) => {
    try {
      await api.post(`/admin/particular/reembolsos/${id}/efetivar`);
      toast.success("Reembolso marcado como processado");
      carregar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmarPagamento = async (leadId) => {
    try {
      await api.post(`/admin/particular/leads/${leadId}/confirmar-pagamento`);
      toast.success("Pagamento confirmado — portal liberado ao cliente");
      carregar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async () => {
    try {
      const r = await api.post("/particular-modulo/toggle");
      setAtivo(r.data.ativo);
      toast.success(r.data.ativo ? "Módulo Cliente Particular ativado!" : "Módulo desativado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const MIN = { orig: 2, mont: 1, montador: 7 };
  const vO = parseFloat(exO) || 0;
  const vMa = parseFloat(exMa) || 0;
  const vMo = parseFloat(exMo) || 0;
  const aDistribuir = (parseFloat(pct) || 0) - 2; // plataforma fica com 2% fixo
  const somaExtras = vO + vMa + vMo;
  const minsOk = vO >= MIN.orig && vMa >= MIN.mont && vMo >= MIN.montador;
  const rateioOk = minsOk && Math.abs(somaExtras - Math.max(0, aDistribuir)) < 0.01;

  const importar = async () => {
    if (!file) { toast.error("Selecione a planilha preenchida"); return; }
    if (!minsOk) { toast.error("Respeite os mínimos: Originador 2%, Montadora 1%, Montador 7%"); return; }
    if (!rateioOk) { toast.error(`A soma dos três percentuais deve fechar em ${Math.max(0, aDistribuir).toFixed(2)}% (total menos 2% da plataforma)`); return; }
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("percentual_total", pct);
      fd.append("extra_originador", (vO - MIN.orig).toFixed(2));
      fd.append("extra_montadora", (vMa - MIN.mont).toFixed(2));
      fd.append("extra_montador", (vMo - MIN.montador).toFixed(2));
      const r = await api.post("/particular/importar", fd);
      toast.success(`${r.data.leads_criados} oferta(s) criada(s) e enviada(s) por WhatsApp`);
      if (r.data.erros?.length) toast.warning(`${r.data.erros.length} pedido(s) com erro`);
      setFile(null);
      carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setEnviando(false);
  };

  return (
    <div className="space-y-5" data-testid="cliente-particular-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Serviço Premium</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1 flex items-center gap-2">
            <Sparkles size={22} className="text-aj-orange" /> Cliente Particular — Ofertar Montagem
          </h1>
          <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
            Importe clientes que ainda não contrataram a montagem. O sistema envia a oferta por WhatsApp,
            apresenta o contrato e controla lembretes por 10 dias. Participação da plataforma: 2% apenas nas vendas deste módulo.
          </p>
        </div>
        <div className="flex items-center gap-2 aj-card px-4 py-3">
          {isAdmin ? (
            <span className="text-xs font-bold uppercase text-aj-text-muted">Visão do administrador</span>
          ) : (
            <>
              <span className="text-xs font-bold uppercase text-aj-text-muted">Módulo {ativo ? "ativo" : "inativo"}</span>
              <Switch checked={!!ativo} onCheckedChange={toggle} data-testid="cp-toggle" />
            </>
          )}
        </div>
      </div>

      {ativo && (
        <div className="aj-card p-5 space-y-4" data-testid="cp-importar-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-bold text-aj-blue-deep">Nova importação</h2>
            <a href="/modelos/MONTAJA_MODELO_CLIENTE_PARTICULAR.xlsx" download
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-aj-orange hover:underline"
              data-testid="cp-baixar-modelo">
              <Download size={15} /> Baixar planilha modelo
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase font-bold text-aj-text-muted">% total da montagem (mín. 12)</label>
              <Input type="number" min="12" step="0.5" value={pct} onChange={e => setPct(e.target.value)} data-testid="cp-pct" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-aj-text-muted">Originador (mín. 2%)</label>
              <Input type="number" min="2" step="0.5" value={exO} onChange={e => setExO(e.target.value)} data-testid="cp-extra-orig" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-aj-text-muted">Montadora (mín. 1%)</label>
              <Input type="number" min="1" step="0.5" value={exMa} onChange={e => setExMa(e.target.value)} data-testid="cp-extra-mont" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-aj-text-muted">Montador (mín. 7%)</label>
              <Input type="number" min="7" step="0.5" value={exMo} onChange={e => setExMo(e.target.value)} data-testid="cp-extra-montador" />
            </div>
          </div>
          <div className={`text-xs font-semibold ${rateioOk ? "text-[#166534]" : "text-[#B91C1C]"}`} data-testid="cp-rateio-status">
            Plataforma: 2% fixo · A distribuir entre os três: {Math.max(0, aDistribuir).toFixed(2)}% · Soma atual: {somaExtras.toFixed(2)}%
            {rateioOk ? " ✓" : !minsOk ? " — respeite os mínimos (2% / 1% / 7%)" : " — a soma dos três deve fechar com o valor a distribuir"}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Input type="file" accept=".xlsx" onChange={e => setFile(e.target.files?.[0] || null)}
              className="max-w-xs" data-testid="cp-file" />
            <Button onClick={importar} disabled={enviando || !file}
              className="bg-aj-blue-deep text-white" data-testid="cp-importar-btn">
              <Upload size={15} className="mr-1.5" /> {enviando ? "Importando…" : "Importar e enviar ofertas"}
            </Button>
          </div>
          <p className="text-[11px] text-aj-text-muted">
            Unidades: mesa + cadeiras = 1 unidade · conjunto de cozinha = 1 unidade · demais produtos individuais.
            Valor por unidade: mínimo R$ 102,00 ou % configurado sobre o valor do produto (o maior).
          </p>
        </div>
      )}

      <div className="aj-card p-5" data-testid="cp-leads-lista">
        <h2 className="font-bold text-aj-blue-deep mb-3">Ofertas ({leads.length})</h2>
        {leads.length === 0 && <div className="text-sm text-aj-text-muted">Nenhuma oferta importada ainda.</div>}
        <div className="space-y-2">
          {leads.map(l => (
            <div key={l.id} className="border border-border rounded-lg px-3 py-2.5 flex items-center justify-between flex-wrap gap-2 text-sm"
              data-testid={`cp-lead-${l.protocolo}`}>
              <div className="flex-1 min-w-[200px]">
                <span className="font-mono text-xs text-aj-text-muted">{l.protocolo}</span>
                <div className="font-semibold text-aj-text">{l.cliente?.nome}</div>
                <div className="text-xs text-aj-text-muted">{l.itens?.length} item(ns) · {l.cliente?.cidade}/{l.cliente?.estado} · pedido {l.pedido_ref}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-aj-blue-deep">R$ {Number(l.valor_montagem_total).toFixed(2).replace(".", ",")}</div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_CORES[l.status] || "bg-gray-100"}`}>
                  {(l.status || "").replace(/_/g, " ")}
                </span>
                {isAdmin && l.status === "AGUARDANDO_PAGAMENTO" && (
                  <div className="mt-1">
                    <button onClick={() => confirmarPagamento(l.id)}
                      className="text-[11px] font-bold text-aj-orange hover:underline"
                      data-testid={`cp-confirmar-pag-${l.protocolo}`}>
                      Confirmar pagamento (manual)
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {prazos.length > 0 && (
        <div className="aj-card p-5" data-testid="cp-prazos">
          <h2 className="font-bold text-aj-blue-deep mb-1 flex items-center gap-2"><Clock3 size={16} /> Prazos dos pedidos abertos ({prazos.length})</h2>
          <p className="text-[11px] text-aj-text-muted mb-3">Prazo operacional de 90 dias a partir do pagamento. Aja antes dos marcos 30/60/80/90.</p>
          <div className="space-y-1.5">
            {prazos.map(p => (
              <div key={p.protocolo} className="flex items-center justify-between flex-wrap gap-2 border border-border rounded-lg px-3 py-2 text-sm"
                data-testid={`cp-prazo-${p.protocolo}`}>
                <div className="flex-1 min-w-[180px]">
                  <span className="font-mono text-xs text-aj-text-muted">{p.protocolo}</span>
                  <div className="font-semibold">{p.cliente}</div>
                  <div className="text-xs text-aj-text-muted">
                    {p.cidade} · {p.produto_entregue ? "produto entregue" : "aguardando entrega"}
                    {p.avisos.length > 0 && ` · avisos: ${p.avisos.join(", ")}d`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${FAIXA_CORES[p.faixa]}`}>
                    {p.dias}d · restam {p.restam}
                  </span>
                  {p.telefone && (
                    <a href={`https://wa.me/${p.telefone.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá, ${(p.cliente || "").split(" ")[0]}! Sobre sua montagem (${p.protocolo}): podemos agendar? 😊`)}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-[#DCFCE7] text-[#166534] hover:opacity-80"
                      data-testid={`cp-contato-${p.protocolo}`}>
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {ressarcimentos.length > 0 && (
        <div className="aj-card p-5" data-testid="cp-ressarcimentos">
          <h2 className="font-bold text-aj-blue-deep mb-1">Ressarcimentos à Loja — reposição de produto ({ressarcimentos.length})</h2>
          <p className="text-xs text-aj-text-muted mb-3">Troca total (Cliente Particular): crédito de 65% do valor do produto destinado à Loja para reposição e reenvio de um novo item ao cliente.</p>
          <div className="space-y-1.5">
            {ressarcimentos.map(r => (
              <div key={r.id} className="border border-border rounded-lg px-3 py-2 text-sm" data-testid={`cp-ress-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="font-mono text-xs text-aj-text-muted">{r.protocolo}</span>
                    <span className="ml-2 text-xs">Loja: <b>{r.loja || "—"}</b></span>
                    <span className="ml-2 text-xs text-aj-text-muted">Produto R$ {Number(r.valor_produto).toFixed(2).replace(".", ",")} · {r.percentual}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-aj-blue-deep">R$ {Number(r.valor_ressarcimento).toFixed(2).replace(".", ",")}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${r.status === "EFETIVADO" ? "bg-[#DCFCE7] text-[#166534]" : r.status === "AUTORIZADO" ? "bg-[#DBEAFE] text-[#142766]" : "bg-[#FEF3C7] text-[#92400E]"}`}>{r.status}</span>
                    {r.is_demo && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">DEMO — sem liquidação</span>}
                    {r.status === "PENDENTE" && (
                      <button onClick={async () => { try { await api.post(`/particular/ressarcimentos/${r.id}/autorizar`); toast.success("Ressarcimento autorizado"); carregar(); } catch (e) { toast.error(formatApiError(e)); } }}
                        className="text-[11px] font-bold text-aj-orange hover:underline" data-testid={`cp-ress-autorizar-${r.id}`}>Autorizar</button>
                    )}
                    {isAdmin && r.status === "AUTORIZADO" && !r.is_demo && (
                      <button onClick={async () => { try { await api.post(`/admin/particular/ressarcimentos/${r.id}/efetivar`); toast.success("Efetivado"); carregar(); } catch (e) { toast.error(formatApiError(e)); } }}
                        className="text-[11px] font-bold text-aj-orange hover:underline" data-testid={`cp-ress-efetivar-${r.id}`}>Efetivar</button>
                    )}
                  </div>
                </div>
                {(() => { const l = leads.find(x => x.id === r.lead_id); return l ? (
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px]">
                    <span className="text-aj-text-muted">Status do caso: <b>{l.status}</b></span>
                    {l.status === "AGUARDANDO_REPOSICAO_PRODUTO" && (
                      <button onClick={async () => { try { await api.post(`/particular/leads/${l.id}/produto-reenviado`); toast.success("OS de reposição liberada"); carregar(); } catch (e) { toast.error(formatApiError(e)); } }}
                        className="font-bold text-aj-orange hover:underline" data-testid={`cp-ress-reenviado-${r.id}`}>Novo produto reenviado/entregue</button>
                    )}
                    {l.aguardando_recolhimento && (
                      <button onClick={async () => { try { await api.post(`/particular/leads/${l.id}/confirmar-recolhimento`); toast.success("Recolhimento confirmado — produto no Pulmão"); carregar(); } catch (e) { toast.error(formatApiError(e)); } }}
                        className="font-bold text-aj-blue-deep hover:underline" data-testid={`cp-ress-recolher-${r.id}`}>Confirmar recolhimento do produto avariado</button>
                    )}
                  </div>
                ) : null; })()}
              </div>
            ))}
          </div>
        </div>
      )}
      {reembolsos.length > 0 && (
        <div className="aj-card p-5" data-testid="cp-reembolsos">
          <h2 className="font-bold text-aj-blue-deep mb-3">Reembolsos ({reembolsos.length})</h2>
          <div className="space-y-1.5">
            {reembolsos.map(r => (
              <div key={r.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm" data-testid={`cp-reembolso-${r.id}`}>
                <div className="flex-1">
                  <span className="font-mono text-xs text-aj-text-muted">{r.protocolo}</span>
                  <span className="ml-2 text-xs text-aj-text-muted">
                    bruto R$ {Number(r.demonstrativo?.valor_bruto).toFixed(2).replace(".", ",")} · retenção R$ {Number(r.demonstrativo?.retencao_20).toFixed(2).replace(".", ",")}
                    {r.demonstrativo?.dentro_arrependimento ? " · arrependimento legal" : ""}
                  </span>
                </div>
                <div className="text-right flex items-center gap-2">
                  <span className="font-bold text-aj-blue-deep">R$ {Number(r.demonstrativo?.valor_reembolso).toFixed(2).replace(".", ",")}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${r.status === "REEMBOLSADO" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>{r.status}</span>
                  {isAdmin && r.status === "PENDENTE" && (
                    <button onClick={() => efetivarReembolso(r.id)}
                      className="text-[11px] font-bold text-aj-orange hover:underline" data-testid={`cp-efetivar-${r.id}`}>
                      Efetivar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="aj-card p-5" data-testid="cp-ledger">
        <h2 className="font-bold text-aj-blue-deep mb-1 flex items-center gap-2"><Wallet size={16} /> Financeiro (ledger D+10)</h2>
        <p className="text-[11px] text-aj-text-muted mb-3">Repasses são liberados automaticamente 10 dias após a data da montagem original.</p>
        {ledger.length === 0 && <div className="text-sm text-aj-text-muted">Nenhum lançamento ainda — os valores aparecem aqui quando a montagem é concluída.</div>}
        <div className="space-y-1.5">
          {ledger.map(e => (
            <div key={e.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm" data-testid={`cp-ledger-${e.id}`}>
              <div className="flex-1">
                <span className="font-mono text-xs text-aj-text-muted">{e.protocolo}</span>
                <span className="ml-2 font-semibold capitalize">{e.participante}</span>
                <span className="ml-2 text-xs text-aj-text-muted">montagem {e.data_montagem?.split("-").reverse().join("/")} · libera {e.liberacao_em?.split("-").reverse().join("/")}</span>
              </div>
              <div className="text-right">
                <span className="font-bold text-aj-blue-deep mr-2">R$ {Number(e.valor).toFixed(2).replace(".", ",")}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${e.status === "LIBERADO" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                  {e.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
